import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Grid, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Alert, Snackbar, FormControl, InputLabel, Select,
  MenuItem, Card, CardContent, Divider, Pagination, CircularProgress, Tooltip,
  Checkbox, FormControlLabel
} from '@mui/material';
import {
  Save as SaveIcon, Cancel as CancelIcon, ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon, Build as BuildIcon,
  CheckBox
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';
import { F_TASKS_HK, EditData, MultiEditModel } from '../../types/stage2';
import PageLayout from '../../components/Layout/PageLayout';
import { useTranslation } from 'react-i18next';

const RFIDMultiEdit: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { selectedTasks } = location.state || { selectedTasks: [] };

  // 狀態管理
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<F_TASKS_HK[]>(selectedTasks || []);
  const [editingData, setEditingData] = useState<F_TASKS_HK[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });

  // 批量編輯表單數據
  const [batchEditData, setBatchEditData] = useState({
    CUBEMARK: '',
    MOULD_NO: '',
    CUBE_TYPE: '',
    MIX_ID: '',
    GRADE: '',
    DEADLINE: '',
    IsUpdateAge: false
  });

  // 初始化編輯數據
  useEffect(() => {
    if (tasks.length > 0) {
      const editDataList: F_TASKS_HK[] = tasks.map(task => ({
        TASK_ID: task.TASK_ID,
        WEB_SUBROUTINES_NAME: task.WEB_SUBROUTINES_NAME,
        CREATED_DATE: task.CREATED_DATE,
        CREATED_BY: task.CREATED_BY,
        UPLOADED_DATE: task.UPLOADED_DATE,
        UPLOADED_BY: task.UPLOADED_BY,
        STATUS: task.STATUS,
        DEADLINE: task.DEADLINE,
        DEADLINE_DATE: task.DEADLINE_DATE,
        CBT_RFD_SN: task.CBT_RFD_SN,
        CUBEMARK: task.CUBEMARK,
        MOULD_NO: task.MOULD_NO,
        CUBE_TYPE: task.CUBE_TYPE,
        EPC_DATA: task.EPC_DATA,
        MIX_ID: task.MIX_ID,
        GRADE: task.GRADE,
        COMPUTER_NAME: task.COMPUTER_NAME,
        TASK_FROM: task.TASK_FROM,
        IsActive: true,
        IsUpdateAge: false,
      }));
      setEditingData(editDataList);
    }
  }, [tasks]);

  // 更新單個任務的編輯數據
  const handleTaskDataChange = (taskId: number, field: keyof EditData, value: any) => {
    setEditingData(prev => prev.map(item =>
      item.TASK_ID === taskId ? { ...item, [field]: value } : item
    ));
  };

  // 應用批量編輯到所有任務
  const handleApplyBatchEdit = () => {
    setEditingData(prev => prev.map(item => ({
      ...item,
      CUBEMARK: batchEditData.CUBEMARK || item.CUBEMARK,
      MOULD_NO: batchEditData.MOULD_NO || item.MOULD_NO,
      CUBE_TYPE: batchEditData.CUBE_TYPE || item.CUBE_TYPE,
      MIX_ID: batchEditData.MIX_ID || item.MIX_ID,
      GRADE: batchEditData.GRADE || item.GRADE,
      DEADLINE_DATE: batchEditData.DEADLINE || item.DEADLINE_DATE,
      DEADLINE: batchEditData.DEADLINE || item.DEADLINE,
      IsUpdateAge: batchEditData.IsUpdateAge || item.IsUpdateAge
    })));

    // 清空批量編輯表單
    setBatchEditData({
      CUBEMARK: '',
      MOULD_NO: '',
      CUBE_TYPE: '',
      MIX_ID: '',
      GRADE: '',
      DEADLINE: '',
      IsUpdateAge: false
    });

    showSnackbar('批量編輯已應用到所有任務', 'success');
  };

  // 比較編輯數據和原始數據，只返回有變更的數據
  const getChangedData = (): F_TASKS_HK[] => {
    return editingData.filter(editedTask => {
      const originalTask = tasks.find(task => task.TASK_ID === editedTask.TASK_ID);
      if (!originalTask) return false;

      // 比較所有可編輯的字段
      const hasChanges =
        editedTask.CUBEMARK !== originalTask.CUBEMARK ||
        editedTask.MOULD_NO !== originalTask.MOULD_NO ||
        editedTask.CUBE_TYPE !== originalTask.CUBE_TYPE ||
        editedTask.MIX_ID !== originalTask.MIX_ID ||
        editedTask.GRADE !== originalTask.GRADE ||
        editedTask.DEADLINE_DATE !== originalTask.DEADLINE_DATE ||
        editedTask.DEADLINE !== originalTask.DEADLINE;

      return hasChanges;
    });
  };

  // 獲取變更統計信息
  const getChangeStatistics = () => {
    const changedData = getChangedData();
    const unchangedCount = editingData.length - changedData.length;

    return {
      total: editingData.length,
      changed: changedData.length,
      unchanged: unchangedCount,
      hasChanges: changedData.length > 0
    };
  };

  // 保存所有編輯
  const handleSaveAll = async () => {
    try {
      setLoading(true);

      const changedData = getChangedData();
      const stats = getChangeStatistics();

      if (!stats.hasChanges) {
        showSnackbar('沒有檢測到任何變更', 'info');
        return;
      }

      const multiEditData: MultiEditModel = {
        Datas: changedData
      };

      console.log('變更統計:', stats);
      console.log('變更的數據:', multiEditData);

      await apiService.multiEditStage2Tasks(multiEditData);
      showSnackbar(`成功批量編輯 ${changedData.length} 個任務（跳過 ${stats.unchanged} 個未變更的任務）`, 'success');

      // 返回上一頁
      navigate(-1);
    } catch (error) {
      console.error('批量編輯失敗:', error);
      showSnackbar('批量編輯失敗', 'error');
    } finally {
      setLoading(false);
    }
  };

  // // 刪除單個任務
  // const handleDeleteTask = (taskId: number) => {
  //   setEditingData(prev => prev.filter(item => item.TASK_ID !== taskId));
  //   setTasks(prev => prev.filter(task => task.TASK_ID !== taskId));
  //   showSnackbar('任務已移除', 'info');
  // };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  if (tasks.length === 0) {
    return (
      <PageLayout title="批量編輯任務">
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              沒有選中的任務
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mt: 2 }}
            >
              返回
            </Button>
          </Box>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="批量編輯任務">
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* 頁面標題和操作按鈕 */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            {t('pages.rfidManagement.batchOperations.batchEdit')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              {t('common.back')}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
              disabled={loading || !getChangeStatistics().hasChanges}
            >
              {t('common.saveChange')} ({getChangeStatistics().changed}/{getChangeStatistics().total})
            </Button>
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('pages.rfidManagement.batchOperations.batchEditMessage').replace('{count}', tasks.length.toString())}
          {/* 正在編輯 {tasks.length} 個任務，您可以直接在表格中修改數據，或使用批量編輯功能 */}
          {getChangeStatistics().hasChanges && (
            <Chip
              label={`${getChangeStatistics().changed} 個任務有變更`}
              color="primary"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Typography>

        {/* 批量編輯工具欄 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {t('pages.rfidManagement.batchOperations.batchEditTool')}
          </Typography>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 600,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
                '&:hover': {
                  background: '#a8a8a8',
                },
              },
            }}
          >
            <TableHead>
              <TableRow sx={{
                backgroundColor: 'primary.main',
                '& .MuiTableCell-head': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  borderBottom: '2px solid',
                  borderBottomColor: 'primary.dark',
                }
              }}>
                <TableCell>Cube Mark</TableCell>
                <TableCell>{t('pages.rfidManagement.fields.mouldNo')}</TableCell>
                <TableCell>Cube Type</TableCell>
                <TableCell>Mix ID</TableCell>
                <TableCell>Grade</TableCell>
                <TableCell>{t('pages.rfidManagement.fields.isUpdateAge')}</TableCell>
                <TableCell>Age day</TableCell>
              </TableRow>
            </TableHead>

            <TableCell>              <TextField
              fullWidth
              size="small"
              label={t('pages.rfidManagement.fields.cubeMark')}
              value={batchEditData.CUBEMARK}
              onChange={(e) => setBatchEditData({ ...batchEditData, CUBEMARK: e.target.value })}
              placeholder={t('common.emptyToKeepOldValue')}
            />
            </TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                label={t('pages.rfidManagement.fields.mouldNo')}
                value={batchEditData.MOULD_NO}
                onChange={(e) => setBatchEditData({ ...batchEditData, MOULD_NO: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                label={t('pages.rfidManagement.fields.cubeType')}
                value={batchEditData.CUBE_TYPE}
                onChange={(e) => setBatchEditData({ ...batchEditData, CUBE_TYPE: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                label="Mix ID"
                value={batchEditData.MIX_ID}
                onChange={(e) => setBatchEditData({ ...batchEditData, MIX_ID: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                label="GRADE"
                value={batchEditData.GRADE}
                onChange={(e) => setBatchEditData({ ...batchEditData, GRADE: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </TableCell>
            <TableCell >
              <Checkbox
                checked={batchEditData.IsUpdateAge}
                onChange={(e) => setBatchEditData({ ...batchEditData, IsUpdateAge: e.target.checked })}
              />
            </TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                label="Age Day"
                type="number"
                inputProps={{ min: 0 }}
                value={batchEditData.DEADLINE}
                onChange={(e) => setBatchEditData({ ...batchEditData, DEADLINE: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </TableCell>
          </TableContainer>


          {/* <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label={t('pages.rfidManagement.fields.cubeMark')}
                value={batchEditData.CUBEMARK}
                onChange={(e) => setBatchEditData({ ...batchEditData, CUBEMARK: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label={t('pages.rfidManagement.fields.mouldNo')}
                value={batchEditData.MOULD_NO}
                onChange={(e) => setBatchEditData({ ...batchEditData, MOULD_NO: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Cube Type"
                value={batchEditData.CUBE_TYPE}
                onChange={(e) => setBatchEditData({ ...batchEditData, CUBE_TYPE: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Mix ID"
                value={batchEditData.MIX_ID}
                onChange={(e) => setBatchEditData({ ...batchEditData, MIX_ID: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="GRADE"
                value={batchEditData.GRADE}
                onChange={(e) => setBatchEditData({ ...batchEditData, GRADE: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Age Day"
                type="number"
                inputProps={{ min: 0 }}
                value={batchEditData.DEADLINE}
                onChange={(e) => setBatchEditData({ ...batchEditData, DEADLINE: e.target.value })}
                placeholder={t('common.emptyToKeepOldValue')}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Checkbox
                checked={batchEditData.IsUpdateAge}
                onChange={(e) => setBatchEditData({ ...batchEditData, IsUpdateAge: e.target.checked })}
              />
            </Grid>
          </Grid> */}
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              startIcon={<BuildIcon />}
              onClick={handleApplyBatchEdit}
              disabled={!batchEditData.CUBEMARK && !batchEditData.MOULD_NO && !batchEditData.CUBE_TYPE && !batchEditData.MIX_ID && !batchEditData.GRADE && !batchEditData.DEADLINE && !batchEditData.IsUpdateAge}
            >
              {t('common.applyToAll')}
            </Button>
          </Box>
        </Paper>

        {/* 任務編輯表格 */}
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 600,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{
                backgroundColor: 'primary.main',
                '& .MuiTableCell-head': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  borderBottom: '2px solid',
                  borderBottomColor: 'primary.dark',
                }
              }}>
                <TableCell>RFID</TableCell>
                <TableCell>Cube Mark</TableCell>
                <TableCell>{t('pages.rfidManagement.fields.mouldNo')}</TableCell>
                <TableCell>Cube Type</TableCell>
                <TableCell>Mix ID</TableCell>
                <TableCell>Grade</TableCell>
                <TableCell>{t('pages.rfidManagement.fields.createdDate')}</TableCell>
                <TableCell>{t('pages.rfidManagement.fields.isUpdateAge')}</TableCell>
                <TableCell>Age day</TableCell>
                {/* <TableCell>{t('common.action')}</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {editingData.map((task, index) => (
                <TableRow
                  key={task.TASK_ID}
                  sx={{
                    backgroundColor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                    transition: 'all 0.2s ease-in-out',
                    '& .MuiTableCell-root': {
                      borderBottom: '1px solid',
                      borderBottomColor: 'divider',
                      padding: '8px 16px',
                    }
                  }}
                >
                  <TableCell sx={{
                    fontFamily: 'monospace',
                    fontWeight: 'medium',
                    color: 'primary.main'
                  }}>
                    {task.CBT_RFD_SN}
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={task.CUBEMARK}
                      onChange={(e) => handleTaskDataChange(task.TASK_ID, 'CUBEMARK', e.target.value)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={task.MOULD_NO}
                      onChange={(e) => handleTaskDataChange(task.TASK_ID, 'MOULD_NO', e.target.value)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={task.CUBE_TYPE}
                      onChange={(e) => handleTaskDataChange(task.TASK_ID, 'CUBE_TYPE', e.target.value)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={task.MIX_ID}
                      onChange={(e) => handleTaskDataChange(task.TASK_ID, 'MIX_ID', e.target.value)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={task.GRADE}
                      onChange={(e) => handleTaskDataChange(task.TASK_ID, 'GRADE', e.target.value)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {task.CREATED_DATE ? new Date(task.CREATED_DATE).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={task.IsUpdateAge}
                      onChange={(e) => handleTaskDataChange(task.TASK_ID, 'IsUpdateAge', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ min: 0 }}
                      value={task.DEADLINE}
                      onChange={(e) => {
                        handleTaskDataChange(task.TASK_ID, 'DEADLINE', e.target.value)
                        handleTaskDataChange(task.TASK_ID, 'DEADLINE_DATE', e.target.value)
                      }}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 提示信息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </PageLayout>
  );
};

export default RFIDMultiEdit;